import { useQuery } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { errorFromResponse } from '@/lib/errors';
import { cleanQuery, type OrdersQueryParams } from './useOrdersQuery';

/**
 * QA-541 주문 목록 검색 결과 합계 — `GET /admin/v1/orders/summary`(목록과 같은 검색 조건, 페이지 무관).
 * 금액은 원화(KRW) 합계. BE 반영 전(404·501)에는 null 을 돌려 합계 줄을 숨긴다.
 */
export interface OrdersSummary {
  orderCount: number;
  itemQuantity: number;
  itemAmount: number;
  shippingFee: number;
  paymentAmount: number;
}

const PATH = '/api/admin/v1/orders/summary';

/** 목록 쿼리와 같은 조건을 쿼리스트링으로(배열은 반복 파라미터). page·size·sort·includeLines 는 합계와 무관해 뺀다. */
export const summaryQueryString = (params: OrdersQueryParams): string => {
  const { page: _p, size: _s, sort: _o, includeLines: _l, ...filters } = params;
  const query = cleanQuery(filters) as Record<string, unknown>;
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (Array.isArray(value)) value.forEach((v) => qs.append(key, String(v)));
    else if (value != null && value !== '') qs.set(key, String(value));
  }
  return qs.toString();
};

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

export const useOrdersSummaryQuery = (params: OrdersQueryParams) => {
  const qs = summaryQueryString(params);
  return useQuery<OrdersSummary | null>({
    queryKey: ['admin', 'orders', 'summary', qs],
    queryFn: async () => {
      const res = await adminFetch(qs ? `${PATH}?${qs}` : PATH);
      if (res.status === 404 || res.status === 501) return null;
      if (!res.ok) throw await errorFromResponse(res);
      const env = (await res.json()) as { data?: Partial<Record<keyof OrdersSummary, unknown>> | null };
      const d = env.data;
      if (!d) return null;
      return {
        orderCount: num(d.orderCount),
        itemQuantity: num(d.itemQuantity),
        itemAmount: num(d.itemAmount),
        shippingFee: num(d.shippingFee),
        paymentAmount: num(d.paymentAmount),
      };
    },
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
};
